import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import idseatLogo from '@/assets/idseat-logo.svg';
import { validatePassword } from '@/lib/password';

// Standalone page (outside the admin shell) that a Supabase password-recovery
// link lands on. detectSessionInUrl (default) exchanges the recovery token in
// the URL for a session, after which updateUser({ password }) is authorized.
type Status = 'checking' | 'ready' | 'expired';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  // If the account has a verified TOTP factor, Supabase requires an aal2 session
  // to change the password. The recovery link only yields aal1, so we step up
  // with a 6-digit challenge before showing the password form.
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);

  useEffect(() => {
    let mounted = true;

    // A stale/used link comes back with an error in the URL hash — treat that
    // as expired immediately without waiting for the session probe.
    const hash = window.location.hash || '';
    if (hash.includes('error=') || hash.includes('error_code=')) {
      setStatus('expired');
      return;
    }

    // The recovery session is created asynchronously as detectSessionInUrl
    // parses the hash. Listen for the recovery event, and also probe the
    // session directly (with one delayed re-check) so we don't hang forever.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION'))) {
        setStatus('ready');
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session) {
        setStatus('ready');
        return;
      }
      // Give detectSessionInUrl a moment to finish, then decide.
      setTimeout(() => {
        supabase.auth.getSession().then(({ data: { session: s } }) => {
          if (!mounted) return;
          setStatus((prev) => (prev === 'ready' ? prev : s ? 'ready' : 'expired'));
        });
      }, 1500);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Once the recovery session exists, check whether a 2FA step-up is required to
  // change the password (true when the account has a verified TOTP factor).
  useEffect(() => {
    if (status !== 'ready') return;
    let active = true;
    (async () => {
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!active) return;
        if (aal?.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const totp = factors?.totp?.[0];
          if (active && totp) {
            setMfaFactorId(totp.id);
            setNeedsMfa(true);
          }
        }
      } catch {
        // If the probe fails, fall through — updateUser will surface any error.
      }
    })();
    return () => {
      active = false;
    };
  }, [status]);

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length !== 6) return;
    setMfaVerifying(true);
    setMfaError(null);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });
      if (chErr || !challenge) throw chErr ?? new Error('Challenge failed');

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: mfaCode,
      });
      if (verifyErr) throw verifyErr;

      // Session is now aal2 — updateUser({ password }) is authorized.
      setMfaVerified(true);
    } catch {
      setMfaError('That code did not match. Check your authenticator app and try again.');
      setMfaCode('');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const check = validatePassword(password);
    if (!check.ok) {
      toast.error(check.message || 'Password does not meet the requirements');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast.success('Password updated — please sign in with your new password');
      // Sign out the temporary recovery session, then send them to login.
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update password');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={idseatLogo} alt="IDSeat" className="h-12" />
          </div>
          <CardTitle className="text-2xl">Set a new password</CardTitle>
          <CardDescription>Choose a strong password for your account</CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'checking' && (
            <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Verifying your reset link…</p>
            </div>
          )}

          {status === 'expired' && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                This password reset link is invalid or has expired. Reset links are
                single-use and time-limited — please request a new one.
              </p>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Back to sign in
              </Button>
            </div>
          )}

          {status === 'ready' && needsMfa && !mfaVerified && (
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-11 h-11 rounded-[12px] bg-primary/10 text-primary flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <p className="text-[14px] text-muted-foreground">
                  Your account has two-factor authentication. Enter the 6-digit code from your
                  authenticator app to continue.
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={setMfaCode}
                  autoFocus
                  disabled={mfaVerifying}
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="rounded-[9px]" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                {mfaError && <p className="text-[13px] text-destructive">{mfaError}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={mfaVerifying || mfaCode.length !== 6}>
                {mfaVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </form>
          )}

          {status === 'ready' && (!needsMfa || mfaVerified) && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 10 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Use at least 10 characters, including a letter and a number.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repeat new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Update password
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
